import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { GET_SOCIAL_PROFILES } from '../graphql';
import { GetSocialProfilesResponse } from '../types/graphql';
import { useAccount, UserAccount } from './AccountContext';

interface ProfileSelectionContextType {
  selectedProfiles: Set<string>;
  setSelectedProfiles: (profiles: Set<string>) => void;
  selectedAccount: UserAccount | null;
  setSelectedAccount: (account: UserAccount | null) => void;
  initializeProfiles: (accountId: string | null) => void;
}

const ProfileSelectionContext = createContext<ProfileSelectionContextType | null>(null);

interface ProfileSelectionProviderProps {
  children: ReactNode;
}

const getProfileStorageKey = (accountId: string | null) => {
  return accountId ? `selected_profiles_${accountId}` : 'selected_profiles';
};

export const ProfileSelectionProvider: React.FC<ProfileSelectionProviderProps> = ({ children }) => {
  // Account selection is owned by AccountContext; this provider only manages
  // per-account social profile filters.
  const { selectedAccount, setSelectedAccount } = useAccount();
  const [selectedProfiles, setSelectedProfilesState] = useState<Set<string>>(new Set());
  const [hasInitialized, setHasInitialized] = useState(false);

  const { data: socialProfilesData } = useQuery<GetSocialProfilesResponse>(GET_SOCIAL_PROFILES, {
    variables: { accountId: selectedAccount?.id },
    skip: !selectedAccount?.id,
  });

  // Persist profile selection per account.
  useEffect(() => {
    if (selectedAccount?.id && selectedProfiles.size > 0) {
      const storageKey = getProfileStorageKey(selectedAccount.id);
      localStorage.setItem(storageKey, JSON.stringify(Array.from(selectedProfiles)));
    } else if (selectedAccount?.id) {
      const storageKey = getProfileStorageKey(selectedAccount.id);
      localStorage.removeItem(storageKey);
    }
  }, [selectedProfiles, selectedAccount?.id]);

  // Initialize profiles with stored or all-active profiles when they load.
  useEffect(() => {
    if (
      socialProfilesData?.socialProfiles &&
      socialProfilesData.socialProfiles.length > 0 &&
      selectedAccount?.id &&
      !hasInitialized
    ) {
      const activeProfiles = socialProfilesData.socialProfiles.filter(
        (profile) => profile.isActive
      );
      const activeProfileIds = activeProfiles.map((profile) => profile.id);

      if (activeProfileIds.length > 0) {
        const storageKey = getProfileStorageKey(selectedAccount.id);
        const storedProfileIds = localStorage.getItem(storageKey);

        if (storedProfileIds) {
          try {
            const storedIds = JSON.parse(storedProfileIds);
            const validStoredIds = (Array.isArray(storedIds) ? storedIds : []).filter((id) =>
              activeProfileIds.includes(id)
            );
            if (validStoredIds.length > 0) {
              setSelectedProfilesState(new Set(validStoredIds));
              setHasInitialized(true);
              return;
            }
          } catch {
            // fall through to default
          }
        }

        setSelectedProfilesState(new Set(activeProfileIds));
        setHasInitialized(true);
      }
    }
  }, [socialProfilesData, selectedAccount, hasInitialized]);

  // Reset profile init when account changes; restore stored selection if any.
  useEffect(() => {
    if (selectedAccount?.id) {
      setHasInitialized(false);
      const storageKey = getProfileStorageKey(selectedAccount.id);
      const storedProfileIds = localStorage.getItem(storageKey);
      if (storedProfileIds) {
        try {
          const storedIds = JSON.parse(storedProfileIds);
          if (Array.isArray(storedIds) && storedIds.length > 0) {
            setSelectedProfilesState(new Set(storedIds));
          } else {
            setSelectedProfilesState(new Set());
          }
        } catch {
          setSelectedProfilesState(new Set());
        }
      } else {
        setSelectedProfilesState(new Set());
      }
    }
  }, [selectedAccount?.id]);

  const setSelectedProfiles = (profiles: Set<string>) => {
    setSelectedProfilesState(profiles);
  };

  const initializeProfiles = (_accountId: string | null) => {
    // Initialization is handled by the effects above.
  };

  return (
    <ProfileSelectionContext.Provider
      value={{
        selectedProfiles,
        setSelectedProfiles,
        selectedAccount,
        setSelectedAccount,
        initializeProfiles,
      }}
    >
      {children}
    </ProfileSelectionContext.Provider>
  );
};

export const useProfileSelection = (): ProfileSelectionContextType => {
  const context = useContext(ProfileSelectionContext);
  if (!context) {
    throw new Error('useProfileSelection must be used within a ProfileSelectionProvider');
  }
  return context;
};
